/**
 * SageLearnerInsight — AI-generated learner profile card.
 * Used on: /learn, parent dashboard, church member progress, bible college.
 *
 * 2026 rebuild: dark-slate surface, electric-blue accent (the platform
 * highlight), two-column strengths/areas, glass chips. Theming API unchanged —
 * accentColor/cardBackground props still honored; luminance flip retained so
 * legacy light-card call sites keep working.
 */
import { motion } from 'framer-motion'
import {
  Sparkles, TrendingUp, Target, AlertCircle, Award,
  Zap, RefreshCw, Repeat, Microscope, Wrench, Lightbulb,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface LearnerProfile {
  summary: string
  strengths: string[]
  struggles: string[]
  learning_pace: string
  engagement_style: string
  last_milestone?: string
  last_updated?: string
  overseer_version?: number
  completed_subjects?: string[]
  weak_concepts?: string[]
}

interface SageLearnerInsightProps {
  profile: LearnerProfile | null | undefined
  learnerName?: string
  accentColor?: string    // expects #RRGGBB
  cardBackground?: string // expects #RRGGBB
}

const BLUE = '#0EA5E9'   // platform highlight — was gold

// ── colour helpers (unchanged — they were the good part) ──
function hex6(hex: string): string {
  const h = (hex || '').replace('#', '')
  return h.length === 3 ? h.split('').map(c => c + c).join('') : h.slice(0, 6)
}
function isDarkBg(hex: string): boolean {
  const h = hex6(hex)
  const r = parseInt(h.slice(0, 2), 16) || 0
  const g = parseInt(h.slice(2, 4), 16) || 0
  const b = parseInt(h.slice(4, 6), 16) || 0
  return (0.299 * r + 0.587 * g + 0.114 * b) < 150
}
function towardBlack(hex: string, amt: number): string {
  const h = hex6(hex)
  const c = (i: number) => Math.round((parseInt(h.slice(i, i + 2), 16) || 0) * (1 - amt))
    .toString(16).padStart(2, '0')
  return `#${c(0)}${c(2)}${c(4)}`
}

const PACE_META: Record<string, { label: string; icon: LucideIcon }> = {
  fast: { label: 'Fast learner', icon: Zap },
  steady: { label: 'Steady & consistent', icon: TrendingUp },
  deliberate: { label: 'Careful & deliberate', icon: Target },
  variable: { label: 'Variable pace', icon: RefreshCw },
  'rushes-then-corrects': { label: 'Fast, self-corrects', icon: Repeat },
}
const STYLE_META: Record<string, { label: string; icon: LucideIcon }> = {
  'deep-diver': { label: 'Deep diver', icon: Microscope },
  practical: { label: 'Practical learner', icon: Wrench },
  conceptual: { label: 'Conceptual thinker', icon: Lightbulb },
  'rushes-then-corrects': { label: 'Trial & error', icon: Repeat },
}

interface Theme {
  text: string; body: string; muted: string
  line: string; chipBg: string; accent: string; accentSoft: string
}

function Chip({ icon: Icon, label, kind, T, accentHex }: {
  icon?: LucideIcon; label: string; kind: 'accent' | 'neutral'; T: Theme; accentHex: string
}) {
  return (
    <span style={{
      fontSize: 12, fontWeight: kind === 'accent' ? 700 : 600,
      padding: '5px 12px', borderRadius: 10,
      display: 'inline-flex', alignItems: 'center', gap: 6,
      color: kind === 'accent' ? T.accent : T.muted,
      background: kind === 'accent' ? `#${accentHex}14` : T.chipBg,
      border: `1px solid ${kind === 'accent' ? `#${accentHex}38` : T.line}`,
    }}>
      {Icon && <Icon size={11} />} {label}
    </span>
  )
}

export function SageLearnerInsight({
  profile,
  learnerName = 'this learner',
  accentColor = BLUE,
  cardBackground = '#0A1322',
}: SageLearnerInsightProps) {
  const { t } = useTranslation()
  if (!profile?.summary) return null

  const dark = isDarkBg(cardBackground)
  const T: Theme = {
    text:   dark ? '#F2F5FA' : '#1B2437',
    body:   dark ? '#C6CEDD' : '#3D4658',
    muted:  dark ? '#A6AEC0' : '#5A6270',
    line:   dark ? 'rgba(255,255,255,0.09)' : '#E9E4DA',
    chipBg: dark ? 'rgba(255,255,255,0.05)' : '#F5F3EE',
    accent: dark ? accentColor : towardBlack(accentColor, 0.38),
    accentSoft: dark ? `#${hex6(accentColor)}1A` : `#${hex6(accentColor)}10`,
  }
  const accentHex = hex6(accentColor)
  const pace = PACE_META[profile.learning_pace]
  const style = STYLE_META[profile.engagement_style]
  const hasStrengths = (profile.strengths?.length ?? 0) > 0
  const hasStruggles = (profile.struggles?.length ?? 0) > 0

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      style={{
        background: `linear-gradient(160deg, ${cardBackground} 0%, #060B16 100%)`,
        border: `1px solid #${accentHex}26`,
        borderRadius: 20, padding: '26px 28px 22px',
        position: 'relative', overflow: 'hidden', marginBottom: 24,
        boxShadow: dark ? `0 20px 60px -30px #${accentHex}30` : 'none',
      }}>
      {/* subtle accent glow, top-left — replaces the giant watermark */}
      <div aria-hidden style={{
        position: 'absolute', top: -80, left: -60, width: 260, height: 200,
        background: `radial-gradient(ellipse, #${accentHex}14 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ background: T.accentSoft, border: `1px solid #${accentHex}30`, padding: 7, borderRadius: 10, display: 'flex' }}>
            <TrendingUp size={15} color={T.accent} />
          </div>
          <span style={{ fontSize: 11, fontWeight: 800, color: T.accent, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            {t('insight.aiInsight', 'AI Learner Insight')}
          </span>
          {profile.overseer_version && (
            <span style={{ marginLeft: 'auto', fontSize: 10.5, color: T.muted, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: T.chipBg, border: `1px solid ${T.line}` }}>
              v{profile.overseer_version}
            </span>
          )}
        </div>

        {/* Summary — the hero */}
        <p style={{ fontSize: 14.5, color: T.body, lineHeight: 1.7, margin: '0 0 14px', maxWidth: 680 }}>
          {profile.summary}
        </p>

        {/* Pace + style */}
        {(pace || style) && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: hasStrengths || hasStruggles ? 20 : 0 }}>
            {pace && <Chip icon={pace.icon} label={pace.label} kind="accent" T={T} accentHex={accentHex} />}
            {style && <Chip icon={style.icon} label={style.label} kind="neutral" T={T} accentHex={accentHex} />}
          </div>
        )}

        {/* Strengths | Areas — two columns on desktop */}
        {(hasStrengths || hasStruggles) && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 18 }}>
            {hasStrengths && (
              <div>
                <p style={{ fontSize: 10.5, fontWeight: 800, color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase', margin: '0 0 9px' }}>
                  {t('insight.strengths', 'Strengths')}
                </p>
                <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                  {profile.strengths.slice(0, 4).map((s, i) => (
                    <Chip key={i} icon={Target} label={s} kind="accent" T={T} accentHex={accentHex} />
                  ))}
                </div>
              </div>
            )}
            {hasStruggles && (
              <div>
                <p style={{ fontSize: 10.5, fontWeight: 800, color: T.muted, letterSpacing: '0.12em', textTransform: 'uppercase', margin: '0 0 9px' }}>
                  {t('insight.areasToGrow', 'Areas to Nurture')}
                </p>
                <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                  {profile.struggles.slice(0, 3).map((s, i) => (
                    <Chip key={i} icon={AlertCircle} label={s} kind="neutral" T={T} accentHex={accentHex} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Milestone footer */}
        {profile.last_milestone && (
          <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${T.line}`, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Award size={13} color={T.accent} />
            <span style={{ fontSize: 12, color: T.muted, fontStyle: 'italic' }}>
              {t('insight.lastMilestone', 'Last milestone')}: {profile.last_milestone}
            </span>
          </div>
        )}
      </div>
    </motion.div>
  )
}